import React from "react";
import BackButton from "@/components/BackButton";

/**
 * Standard page shell (DEV-003F).
 *
 * `<PageContainer width="narrow|medium|full">` — wraps every non-root
 * page body in one of three responsive width buckets:
 *
 *   • narrow — About, Profile, Login, Change/Forgot/Reset Password
 *              (max-w-3xl ≈ 768px)
 *   • medium — Administration, Documents, Journey, Messages,
 *              Appointments, My System (max-w-[1200px])
 *   • full   — Operations/Installer workspaces, master/detail pages
 *              (max-w-[1600px])
 *
 * `<PageHeader>` — one component for every page header. Handles the
 * Back button, eyebrow, icon, title and subtitle with identical
 * spacing and typography across Customer, Installer and Operations.
 *
 * These two components should be **the only way** pages render their
 * top-of-page chrome. Do not roll your own — regressions land here.
 */

const WIDTH_CLASS = {
    narrow: "max-w-3xl",         // ≈ 768px
    medium: "max-w-[1200px]",     // 1200px
    full:   "max-w-[1600px]",     // 1600px
};

export function PageContainer({ width = "full", className = "", children, testid }) {
    const widthClass = WIDTH_CLASS[width] || WIDTH_CLASS.full;
    return (
        <div
            className={`mx-auto w-full ${widthClass} px-4 md:px-6 lg:px-8 py-6 md:py-8 ${className}`}
            data-testid={testid}
        >
            {children}
        </div>
    );
}

export function PageHeader({
    eyebrow,
    title,
    subtitle,
    icon: Icon,
    showBack = true,
    backTo,
    backFallback = "/",
    backLabel,
    rightSlot,
    className = "",
    testid = "page-header",
}) {
    return (
        <header className={`mb-6 md:mb-8 ${className}`} data-testid={testid}>
            {showBack && (
                <div className="mb-4">
                    <BackButton
                        to={backTo}
                        fallbackHref={backFallback}
                        label={backLabel || "Back"}
                        testid={`${testid}-back`}
                    />
                </div>
            )}
            <div className="flex items-start gap-4">
                {Icon && (
                    <span className="w-10 h-10 md:w-11 md:h-11 rounded-md bg-[#131B2F] text-white flex items-center justify-center shrink-0" data-testid={`${testid}-icon`}>
                        <Icon size={18} strokeWidth={1.75} />
                    </span>
                )}
                <div className="flex-1 min-w-0">
                    {eyebrow && (
                        <div className="text-[11px] tracking-[0.14em] uppercase text-[#4B5565] font-medium" data-testid={`${testid}-eyebrow`}>
                            {eyebrow}
                        </div>
                    )}
                    <h1
                        className="mt-1 text-2xl md:text-3xl lg:text-4xl text-[#131B2F] font-medium leading-tight truncate"
                        data-testid={`${testid}-title`}
                    >
                        {title}
                    </h1>
                    {subtitle && (
                        <p className="mt-2 text-sm md:text-base text-[#4B5565] max-w-2xl" data-testid={`${testid}-subtitle`}>
                            {subtitle}
                        </p>
                    )}
                </div>
                {rightSlot && <div className="shrink-0 self-center" data-testid={`${testid}-right`}>{rightSlot}</div>}
            </div>
        </header>
    );
}

export default PageContainer;
